import type { ParsedUrlQuery } from 'querystring';
import React, { useEffect, useMemo } from 'react';
import type { GetStaticPaths, GetStaticPathsResult, GetStaticProps, Redirect } from 'next';
import Router from 'next/router';
import { isAddress } from 'viem/utils';
import {
  BURNED_ADDRESSES,
  TimePeriodAggEnum,
  TrendingCollectorsSortEnum,
} from '@soundxyz/utils/const';
import { parseEns } from '@soundxyz/utils/ens';
import { ArtistsBacked } from '@components/ArtistsBacked';
import { FeaturedSound } from '@components/Profile/FeaturedSound';
import { CollectorHero } from '@components/Profile/ProfileHero/CollectorHero';
import { AboutSection } from '@components/Profile/Sections/About';
import { SoundCollections } from '@components/Profile/Sections/SoundCollections';
import { UserInfo } from '@components/Profile/Sections/UserInfo';
import { Seo } from '@components/Seo';
import { Spinner } from '@components/basics/Spinner';
import type { GetUserProfileNewQuery } from '@graphql/reactQuery';
import {
  fetchGQL,
  getFragment,
  GetUserProfileNewDocument,
  TrendingCollectorsDocument,
  useQuery,
  UserProfileFragmentDoc,
  UserSoundCollectionFragmentDoc,
} from '@graphql/reactQuery';
import { isEns } from '@utils/helpers';
import { getUserOpenGraphImage } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

const ISR_REVALIDATE_PERIOD = 20;

const notFoundPermanentRedirect = {
  redirect: {
    destination: '/404',
    permanent: true,
  },
  revalidate: ISR_REVALIDATE_PERIOD,
};

function staticPropsPublicAddressOrEns({
  params: { publicAddress: publicAddressOrEns } = {},
}: {
  params: ParsedUrlQuery | undefined;
}):
  | ({ redirect: Redirect; revalidate: number | boolean } & { variables?: undefined })
  | {
      redirect?: undefined;
      variables: { publicAddress: string } | { ens: string };
    } {
  if (!publicAddressOrEns || Array.isArray(publicAddressOrEns)) {
    return notFoundPermanentRedirect;
  }

  if (isAddress(publicAddressOrEns)) {
    if (BURNED_ADDRESSES.has(publicAddressOrEns.toLowerCase())) {
      return {
        redirect: {
          destination: '/404',
          permanent: false,
        },
        revalidate: false,
      };
    }

    return {
      variables: {
        publicAddress: publicAddressOrEns.toLowerCase(),
      },
    };
    // If it isn't an address, maybe it is an ENS name
  } else {
    const ens = parseEns(publicAddressOrEns);

    // Ensure it is an ENS name
    if (!ens) return notFoundPermanentRedirect;

    return {
      variables: {
        ens,
      },
    };
  }
}

interface PageProps {
  user: NonNullable<GetUserProfileNewQuery['userByAddress']>;
}

type PathParams = {
  publicAddress: string;
};

export const getStaticProps: GetStaticProps<PageProps, PathParams> = async ({ params = {} }) => {
  const staticProps = staticPropsPublicAddressOrEns({
    params,
  });
  const { redirect, variables } = staticProps;

  if (redirect) return { redirect, revalidate: staticProps.revalidate };

  return Promise.all([
    fetchGQL(GetUserProfileNewDocument, {
      variables,
    }),
  ]).then(async ([res]) => {
    if (!res.data.userByAddress) {
      return {
        redirect: {
          destination: '/404',
          permanent: false,
        },
        revalidate: ISR_REVALIDATE_PERIOD,
      };
    }

    // Artists should be redirected to their profile page
    const user = getFragment(UserProfileFragmentDoc, res.data.userByAddress);

    if (!!user.artist && user.artist.soundHandle) {
      return {
        redirect: {
          destination: webappUriPath(user.artist.webappUri),
          permanent: false,
          revalidate: ISR_REVALIDATE_PERIOD,
        },
      };
    }

    const props: PageProps = {
      user: res.data.userByAddress,
    };

    return {
      props,
      revalidate: ISR_REVALIDATE_PERIOD,
    };
  });
};

export const getStaticPaths: GetStaticPaths<PathParams> = async () => {
  return Promise.all([
    fetchGQL(TrendingCollectorsDocument, {
      variables: {
        sort: TrendingCollectorsSortEnum.CREATORS_SUPPORTED,
        timePeriod: TimePeriodAggEnum.SEVEN_DAY,
      },
    }),
  ]).then(([data]) => {
    return {
      paths: data.data.topCollectorsV2.reduce<GetStaticPathsResult<PathParams>['paths']>(
        (acc, { user }) => {
          if (!!user && !BURNED_ADDRESSES.has(user.publicAddress) && !user.artist?.soundHandle) {
            acc.push({
              params: {
                publicAddress: user.publicAddress,
              },
            });
          }

          return acc;
        },
        [],
      ),
      fallback: true,
    };
  });
};

export default function PublicUserProfile({ user: userProp }: Partial<PageProps>) {
  // When using fallback, we don't have access to params through next router
  const publicAddress = useMemo(() => {
    if (userProp?.publicAddress) return userProp.publicAddress;

    if (typeof window !== 'undefined') {
      const param = new URL(window.location.href).pathname.split('user/')[1];

      if (param && isAddress(param)) return param;
    }

    return null;
  }, [userProp]);

  // When using fallback, we don't have access to params through next router
  const ens = useMemo(() => {
    if (userProp?.ens) return userProp.ens;

    if (typeof window !== 'undefined') {
      const param = new URL(window.location.href).pathname.split('user/')[1];

      if (isEns(param)) return param;
    }

    return null;
  }, [userProp]);

  const { data, isInitialLoading, isError, isRefetchError, isLoadingError, isLoading } = useQuery(
    GetUserProfileNewDocument,
    {
      variables: !!(ens || publicAddress) && {
        ens,
        publicAddress,
      },
      initialData: userProp
        ? () => {
            return {
              data: {
                userByAddress: userProp,
              },
            };
          }
        : undefined,
      staleTime: 0,
    },
  );

  const user = getFragment(UserProfileFragmentDoc, data?.data.userByAddress || userProp);

  const userCollectorFragment = getFragment(UserSoundCollectionFragmentDoc, user);

  const username = user?.username || `${publicAddress?.slice(0, 5)}...${publicAddress?.slice(-4)}`;

  const featuredSound = user?.featuredReleases?.[0];

  const isUser = !!data?.data.userByAddress;

  useEffect(() => {
    if (isUser || isInitialLoading || isError || isRefetchError || isLoadingError) return;

    Router.replace('/404');
  }, [isError, isInitialLoading, isLoadingError, isRefetchError, isUser]);

  // Only used on fallback, when cache can't be found
  if (!user || !userCollectorFragment) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const userOg = getUserOpenGraphImage({ userId: user.id });

  return (
    <>
      <Seo
        title={`${username}'s Sound profile`}
        twitterHandle={user.twitterHandle}
        smallTwitterCard={!userOg && !!user.avatar?.url}
        description={user.description}
        image={userOg || user.avatar?.url}
      />
      <div className="flex min-h-screen flex-col bg-white">
        <div className="w-full bg-white">
          <CollectorHero collectorData={user} />
          <div className="relative -top-14 mx-auto mt-0 flex w-full max-w-7xl flex-col lg:-top-32 lt:px-12">
            <UserInfo userData={user} loading={isLoading} />
            {featuredSound && (
              <FeaturedSound
                userId={user.id}
                featuredReleaseFrag={featuredSound}
                isLoading={isLoading}
              />
            )}
            <ArtistsBacked user={user} />
            <SoundCollections user={user} />
            <AboutSection aboutData={user} loading={isLoading} />
          </div>
        </div>
      </div>
    </>
  );
}
